import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Trash, UploadSimple, ImageSquare } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// SkuImageUploader — drag-drop / click / camera-capture uploader.
// Resizes on the client to 300×300 JPEG @ q=0.82 before uploading so we
// stay well under the server's 400KB base64 limit.
//
// Props:
//   skuId          — required, target SKU document _id
//   currentImage   — optional, existing data URL to show in preview
//   onUploaded(img)— optional callback fired with the new data URL on success
//   compact        — render a smaller preview (used inline on pick cards etc.)
function resizeImageFile(file, max = 300) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const ratio = Math.min(max / img.width, max / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function SkuImageUploader({ skuId, currentImage, onUploaded, compact = false }) {
  const [preview, setPreview] = useState(currentImage || null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const upload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (>10MB original). Please pick a smaller photo.');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeImageFile(file, 300);
      const { data } = await axios.post(
        `${API}/api/skus/${skuId}/image`,
        { image_base64: dataUrl },
        { withCredentials: true }
      );
      setPreview(dataUrl);
      onUploaded?.(dataUrl);
      toast.success(`Image saved · ${Math.round((data?.bytes || dataUrl.length) / 1024)} KB`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const removeImage = async () => {
    if (!window.confirm('Remove image?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/skus/${skuId}/image`, { withCredentials: true });
      setPreview(null);
      onUploaded?.(null);
      toast.success('Image removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  if (compact) {
    return (
      <div className="w-20 h-20 shrink-0 border border-border bg-muted flex items-center justify-center overflow-hidden" data-testid="sku-image-compact">
        {preview ? (
          <img src={preview} alt="SKU" className="w-full h-full object-cover" />
        ) : (
          <ImageSquare size={28} className="text-muted-foreground" />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sku-image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative w-full aspect-square max-w-[300px] border-2 border-dashed flex items-center justify-center overflow-hidden bg-muted transition ${dragOver ? 'border-brand bg-brand/5' : 'border-border'}`}
      >
        {preview ? (
          <img src={preview} alt="SKU preview" className="w-full h-full object-cover" data-testid="sku-image-preview" />
        ) : (
          <div className="text-center p-4 text-muted-foreground">
            <ImageSquare size={40} className="mx-auto mb-2" />
            <p className="text-xs uppercase tracking-widest font-bold">Drop image here</p>
            <p className="text-[10px] mt-1">or use the buttons below · auto-resized to 300×300</p>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="file"
          accept="image/*"
          ref={fileRef}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="sku-image-file-input"
        />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={cameraRef}
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="sku-image-camera-input"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          variant="outline"
          className="rounded-none font-bold uppercase tracking-widest text-xs"
          data-testid="sku-image-upload-btn"
        >
          <UploadSimple size={14} weight="bold" className="mr-1" />
          Choose file
        </Button>
        <Button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={busy}
          variant="outline"
          className="rounded-none font-bold uppercase tracking-widest text-xs md:hidden"
          data-testid="sku-image-camera-btn"
        >
          <Camera size={14} weight="bold" className="mr-1" />
          Camera
        </Button>
        {preview && (
          <Button
            type="button"
            onClick={removeImage}
            disabled={busy}
            variant="outline"
            className="rounded-none font-bold uppercase tracking-widest text-xs border-rose-500 text-rose-600 hover:bg-rose-50"
            data-testid="sku-image-remove-btn"
          >
            <Trash size={14} weight="bold" className="mr-1" />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
